import { jwtDecode } from "jwt-decode";
import { api } from "@workspace/shared/core/services/api";
import { authStore } from "@workspace/shared/core/store/auth/auth.store";

/**
 * Ported from the in-game app (platform-2.0 features/support, client
 * 2026-10-08). The practice apps have their own FAQ set — edited on the
 * admin tool's Practice tab — and their own answers from the assistant, so
 * every FAQ and chat request names the product. The in-game apps send none
 * and get theirs.
 */
const PRODUCT = "practice";

export interface SupportTurn {
  role: "user" | "assistant";
  content: string;
}

export interface SupportVideo {
  url: string;
  /** The entry the video belongs to, used as the player's caption. */
  question: string;
}

export interface SupportAnswer {
  reply: string;
  /** False when the FAQ did not cover it — the UI then offers a ticket. */
  answered: boolean;
  suggestions: string[];
  /** How-to video for the entry the answer came from, if one is attached. */
  video: SupportVideo | null;
}

const CHAT_ENDPOINT = "/support/chat";
const TICKET_ENDPOINT = "/support/ticket";
const FAQ_ENDPOINT = "/support/faq";

/** The model only needs recent context; a whole visit would bloat the payload. */
const MAX_HISTORY_TURNS = 12;

/** A stalled request must not hang the composer forever. */
const CHAT_TIMEOUT_MS = 30_000;

/** The ticket submit needs the same guard, so a stalled sideline
 * connection cannot leave the form stuck on "Sending…" forever. */
const TICKET_TIMEOUT_MS = 30_000;

export const askSupport = async (
  message: string,
  history: SupportTurn[]
): Promise<SupportAnswer> => {
  const response = await api.post(
    CHAT_ENDPOINT,
    { message, history: history.slice(-MAX_HISTORY_TURNS), product: PRODUCT },
    { timeout: CHAT_TIMEOUT_MS }
  );
  const data = response?.data ?? {};
  return {
    reply: data.reply ?? "",
    answered: data.answered === true,
    suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
    video:
      data.video && typeof data.video.url === "string"
        ? { url: data.video.url, question: data.video.question ?? "" }
        : null,
  };
};

export const submitSupportTicket = async (input: {
  email: string;
  name?: string;
  subject: string;
  message: string;
  transcript?: SupportTurn[];
  appKey?: string;
}): Promise<void> => {
  await api.post(TICKET_ENDPOINT, input, {
    timeout: TICKET_TIMEOUT_MS,
  });
};

/**
 * What to tell the coach when a ticket fails. The API refuses an address the
 * browser let through ("coach@school") with a 400: that's the address, not
 * the connection — retrying would never help (client 2026-10-09).
 */
export const ticketErrorMessage = (error: unknown, what: string): string =>
  (error as { response?: { status?: number } } | null)?.response?.status === 400
    ? `${what} could not be sent. Check the email address and try again.`
    : `${what} could not be sent. Check your connection and try again.`;

/** A question that never reached the assistant. */
export const SUPPORT_OFFLINE_ERROR =
  "I could not reach support just now. Check your connection and try again — the assistant needs internet.";
/** Shown only if a 429 somehow arrives without the server's wording. */
const SUPPORT_RATE_LIMITED_FALLBACK =
  "Support is getting too many questions right now. Please wait a few minutes and try again.";
/** Signed in, but this account may not use the assistant (a 403). */
export const SUPPORT_FORBIDDEN_ERROR =
  "Support chat isn't available for your account. Use Contact Us instead.";
/** Signed out mid-chat. */
export const SUPPORT_SIGNED_OUT_ERROR =
  "You were signed out. Sign in again, then send your question again.";
/** The assistant itself is down or failed (a 5xx). */
export const SUPPORT_UNAVAILABLE_ERROR =
  "Support chat isn't available right now. Please try again in a moment.";
/** A refused question the server gave no reason for (a 4xx). */
const SUPPORT_REFUSED_FALLBACK =
  "That question couldn't be sent. Try rephrasing it, or use Contact Us.";
/** Not a network or server answer at all: a bug, say nothing about Wi-Fi. */
const SUPPORT_UNEXPECTED_ERROR =
  "Something went wrong sending your question. Please try again.";

/**
 * What the chat says when a question fails — what actually happened, so the
 * coach knows what to do (ported from the in-game app, review 2026-10-16):
 *
 * - no answer at all → check the connection;
 * - 429 → the rate limit's own words ("…try again in N minutes"); "check
 *   your connection" sent coaches straight back into the same limit;
 * - 401 → sign in again; 403 → not for this account;
 * - another 4xx → the server's reason (e.g. a question too long);
 * - 5xx → support is unavailable, not the coach's Wi-Fi.
 */
export const supportChatErrorMessage = (error: unknown): string => {
  // axios.isAxiosError, without making this package depend on axios.
  const failure = error as {
    isAxiosError?: boolean;
    response?: { status?: number; data?: unknown };
  } | null;
  if (failure?.isAxiosError !== true) return SUPPORT_UNEXPECTED_ERROR;
  const status = failure.response?.status;
  if (!status) return SUPPORT_OFFLINE_ERROR;
  const message = (failure.response?.data as { message?: unknown } | null)
    ?.message;
  // A reason the API wrote for people — not the framework's own words
  // ("Bad Request Exception", "Forbidden resource", "Cannot POST /…"),
  // which a failed validation or a missing route sends.
  const raw = typeof message === "string" ? message.trim() : "";
  const said =
    /exception$|^forbidden resource$|^cannot (get|post|put|patch|delete) /i.test(raw)
      ? ""
      : raw;
  if (status === 429) return said || SUPPORT_RATE_LIMITED_FALLBACK;
  if (status === 401) return SUPPORT_SIGNED_OUT_ERROR;
  if (status === 403) return SUPPORT_FORBIDDEN_ERROR;
  if (status >= 500) return SUPPORT_UNAVAILABLE_ERROR;
  return said || SUPPORT_REFUSED_FALLBACK;
};

export interface SupportFaqEntry {
  question: string;
  answer: string;
  /** How-to video for this entry, if the admin attached one. */
  video: SupportVideo | null;
}

const normalizeVideo = (raw: unknown): SupportVideo | null => {
  if (typeof raw === "string") return raw ? { url: raw, question: "" } : null;
  if (raw && typeof raw === "object") {
    const url = (raw as { url?: unknown }).url;
    if (typeof url === "string" && url) {
      const question = (raw as { question?: unknown }).question;
      return { url, question: typeof question === "string" ? question : "" };
    }
  }
  return null;
};

/**
 * The FAQ entries the team maintains in the admin tool — the SAME entries that
 * feed the AI agent — each with its written answer and (optionally) a how-to
 * video. The FAQ page shows them as cards and opens the answer + video
 * DIRECTLY, so it never has to call the agent. A failed load throws, so the
 * page can tell "couldn't be loaded" from a set that simply has no topics yet
 * (the practice set starts empty).
 */
export const fetchSupportFaq = async (): Promise<SupportFaqEntry[]> => {
  const response = await api.get(FAQ_ENDPOINT, {
    params: { product: PRODUCT },
    timeout: CHAT_TIMEOUT_MS,
  });
  // The API names the set it answered with. One from before there were two
  // sets ignores ?product= and answers with the in-game FAQ — that must never
  // be shown to practice coaches. Anything else that isn't the FAQ (a captive
  // Wi-Fi login page answers 200 with HTML) is a failed load too.
  const raw = response?.data?.entries;
  if (response?.data?.product !== PRODUCT || !Array.isArray(raw)) {
    throw new Error("No practice FAQ in this response");
  }
  return raw
    .map((item): SupportFaqEntry | null => {
      const question = (item as { question?: unknown })?.question;
      if (typeof question !== "string" || !question.trim()) return null;
      const answer = (item as { answer?: unknown })?.answer;
      const videoRaw =
        (item as { video?: unknown }).video ?? (item as { videoUrl?: unknown }).videoUrl;
      return {
        question,
        answer: typeof answer === "string" ? answer : "",
        video: normalizeVideo(videoRaw),
      };
    })
    .filter((entry): entry is SupportFaqEntry => entry !== null);
};

/**
 * Best-effort read of the signed-in coach's address, to prefill the ticket.
 *
 * Only a convenience: the field stays editable, and an unreadable token just
 * means the coach types it. Their address is what support replies to, so it
 * must never be silently wrong — hence no fallback guess.
 */
export const currentUserEmail = (): string => {
  try {
    const token = authStore.getState().idToken;
    if (!token) return "";
    const claims = jwtDecode<{ email?: string }>(token);
    return typeof claims?.email === "string" ? claims.email : "";
  } catch {
    return "";
  }
};

/**
 * Best-effort read of the signed-in coach's first/last name from the token, to
 * prefill the Contact Us form. Empty strings when unknown — the fields stay
 * editable, so a missing name just means they type it.
 */
export const currentUserName = (): { firstName: string; lastName: string } => {
  const empty = { firstName: "", lastName: "" };
  try {
    const token = authStore.getState().idToken;
    if (!token) return empty;
    const claims = jwtDecode<{
      given_name?: string;
      family_name?: string;
      name?: string;
    }>(token);
    const given = typeof claims.given_name === "string" ? claims.given_name : "";
    const family = typeof claims.family_name === "string" ? claims.family_name : "";
    if (given || family) return { firstName: given, lastName: family };
    // Fall back to splitting a single "name" claim ("First Last").
    if (typeof claims.name === "string" && claims.name.trim()) {
      const parts = claims.name.trim().split(/\s+/);
      return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
    }
    return empty;
  } catch {
    return empty;
  }
};
